import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import type { TenantCustomerListItem } from "../lib/customers.js";
import { CustomerCsvImportModal } from "../components/CustomerCsvImportModal.js";
import { CustomerRoleTypesPanel } from "../components/CustomerRoleTypesPanel.js";
import { DefaultCoverageCard } from "../components/DefaultCoverageCard.js";

/**
 * Tenant admin — Customers list (ADR-0075 §3.1).
 *
 * Route: `/app/admin/customers`.
 * Lists tenant_customers with name (indented per hierarchy), external_id,
 * parent, user count, primary user, catchall pill. Toolbar holds search,
 * parent filter, and three action buttons.
 */
export function Customers() {
  const nav = useNavigate();
  const toast = useToast();
  const { confirm } = useConfirm();

  const [customers, setCustomers] = useState<TenantCustomerListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [parentFilter, setParentFilter] = useState<string>("");
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [rolesOpen, setRolesOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Tree-view fold state. Stored per-customer-id; default collapsed.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggleExpanded(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch.trim() !== "") params.set("q", debouncedSearch.trim());
      if (parentFilter !== "") params.set("parent_id", parentFilter);
      const qs = params.toString();
      const res = await apiFetch<{ customers: TenantCustomerListItem[] }>(
        `/api/tenant/admin/customers${qs ? `?${qs}` : ""}`
      );
      setCustomers(res.customers);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, parentFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const topLevelOptions = customers.filter((c) => c.parent_customer_id === null);

  async function deleteCustomer(c: TenantCustomerListItem) {
    const ok = await confirm({
      title: `Delete "${c.display_name}"?`,
      detail: "Sub-customers must be reparented or deleted first.",
      confirmLabel: "Delete"
    });
    if (!ok) return;
    setBusy((p) => ({ ...p, [c.id]: true }));
    try {
      await apiFetch(`/api/tenant/admin/customers/${c.id}`, { method: "DELETE" });
      toast.push({ kind: "success", message: "Customer deleted." });
      await load();
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
      toast.push({ kind: "error", message: msg });
    } finally {
      setBusy((p) => ({ ...p, [c.id]: false }));
    }
  }

  return (
    <PageLayout title="Account Coverage">
      <div style={{ display: "grid", gap: 12 }}>
        <DefaultCoverageCard />

        <div className="card">
          <div className="toolbar">
            <input
              className="input"
              type="text"
              placeholder="Search by name or account code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: "0 0 280px" }}
            />
            <select
              className="select"
              value={parentFilter}
              onChange={(e) => setParentFilter(e.target.value)}
              style={{ flex: "0 0 220px" }}
            >
              <option value="">All customers</option>
              <option value="__none__">Top-level only</option>
              {topLevelOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  Sub-customers of {c.display_name}
                </option>
              ))}
            </select>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button className="btn" onClick={() => setRolesOpen(true)}>
                Manage roles
              </button>
              <button className="btn" onClick={() => setImportOpen(true)}>
                Import
              </button>
              <Link className="btn btnPrimary" to="/app/admin/customers/new">
                + New customer
              </Link>
            </div>
          </div>

          {error && <ErrorState detail={error} />}
          {loading && !error && <LoadingState />}
          {!loading && !error && customers.length === 0 && (
            <div className="muted" style={{ padding: 24 }}>
              No customers yet. Create the first one with <strong>+ New customer</strong>, or use{" "}
              <strong>Import</strong> to seed in bulk from CSV or Excel.
            </div>
          )}
          {!loading && !error && customers.length > 0 && (() => {
            const isFlat = debouncedSearch.trim() !== "" || parentFilter !== "";

            // Group children by parent for the tree view.
            const childrenByParent = new Map<string, TenantCustomerListItem[]>();
            for (const c of customers) {
              if (c.parent_customer_id) {
                const arr = childrenByParent.get(c.parent_customer_id) ?? [];
                arr.push(c);
                childrenByParent.set(c.parent_customer_id, arr);
              }
            }
            const topLevel = customers.filter((c) => c.parent_customer_id === null);

            const renderRow = (c: TenantCustomerListItem, depth: number): React.ReactNode[] => {
              const kids = childrenByParent.get(c.id) ?? [];
              const hasKids = kids.length > 0;
              const isExpanded = expanded.has(c.id);
              const rows: React.ReactNode[] = [
                <tr key={c.id}>
                  <td style={{ paddingLeft: 8 + depth * 24 }}>
                    {hasKids ? (
                      <button
                        type="button"
                        onClick={() => toggleExpanded(c.id)}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                        style={{
                          display: "inline-block",
                          width: 18,
                          marginRight: 4,
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "inherit",
                          padding: 0,
                          fontSize: 12
                        }}
                      >
                        {isExpanded ? "▼" : "▶"}
                      </button>
                    ) : (
                      <span style={{ display: "inline-block", width: 18, marginRight: 4 }} />
                    )}
                    <Link to={`/app/admin/customers/${c.id}`}>
                      <span className="mono">{c.external_id}</span>: {c.display_name}
                    </Link>
                    {hasKids && (
                      <span className="muted" style={{ marginLeft: 6 }}>
                        ({kids.length})
                      </span>
                    )}
                  </td>
                  <td className="mono">{c.external_id}</td>
                  <td className="muted">{c.parent_display_name ?? "—"}</td>
                  <td style={{ textAlign: "right" }}>{c.user_count}</td>
                  <td className="muted">{c.primary_display_name ?? "—"}</td>
                  <td>
                    <button
                      className="adminListAction isDanger"
                      onClick={() => void deleteCustomer(c)}
                      disabled={busy[c.id]}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ];
              if (hasKids && isExpanded) {
                for (const kid of kids) rows.push(...renderRow(kid, depth + 1));
              }
              return rows;
            };

            return (
              <>
                {!isFlat && customers.some((c) => c.parent_customer_id !== null) && (
                  <div style={{ display: "flex", gap: 12, marginBottom: 6, fontSize: 13 }}>
                    <button
                      type="button"
                      className="muted"
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => setExpanded(new Set(customers.filter((c) => (childrenByParent.get(c.id) ?? []).length > 0).map((c) => c.id)))}
                    >
                      Expand all
                    </button>
                    <button
                      type="button"
                      className="muted"
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => setExpanded(new Set())}
                    >
                      Collapse all
                    </button>
                  </div>
                )}
                <div className="tableWrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Account Code</th>
                        <th>Parent customer</th>
                        <th style={{ textAlign: "right" }}>Users</th>
                        <th>Primary</th>
                        <th style={{ width: 100 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {isFlat
                        ? customers.map((c) => (
                            <tr key={c.id}>
                              <td style={{ paddingLeft: c.parent_customer_id ? 24 : undefined }}>
                                <Link to={`/app/admin/customers/${c.id}`}>
                                  <span className="mono">{c.external_id}</span>: {c.display_name}
                                </Link>
                              </td>
                              <td className="mono">{c.external_id}</td>
                              <td className="muted">{c.parent_display_name ?? "—"}</td>
                              <td style={{ textAlign: "right" }}>{c.user_count}</td>
                              <td className="muted">{c.primary_display_name ?? "—"}</td>
                              <td>
                                <button
                                  className="adminListAction isDanger"
                                  onClick={() => void deleteCustomer(c)}
                                  disabled={busy[c.id]}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))
                        : topLevel.flatMap((c) => renderRow(c, 0))}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {rolesOpen && (
        <CustomerRoleTypesPanel
          onClose={() => {
            setRolesOpen(false);
          }}
        />
      )}
      {importOpen && (
        <CustomerCsvImportModal
          onClose={() => setImportOpen(false)}
          onApplied={() => {
            setImportOpen(false);
            void load();
          }}
        />
      )}
    </PageLayout>
  );
}
